import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface AdminProductsFilter {
  selectedCategoriesFilter: string[];
  selectedMakesFilter: string[];
  searchAdminProductsTerm: string;
  gridView: boolean;
}

interface AdminProductsFiltersState {
  // State
  filters: AdminProductsFilter;

  // Actions
  setFilters: (newFilters: Partial<AdminProductsFilter>) => void;
  clearFilters: () => void;
  setGridView: (gridView: boolean) => void;
  setSearchTerm: (term: string) => void;
  setCategoryFilters: (categories: string[]) => void;
  setMakeFilters: (makes: string[]) => void;
}

const initialFilters: AdminProductsFilter = {
  selectedCategoriesFilter: [],
  selectedMakesFilter: [],
  searchAdminProductsTerm: "",
  gridView: true,
};

// DevTools configuration options
const devToolsOptions = {
  // Name of the store in Redux DevTools (helps when you have multiple stores)
  name: "AdminProductsFilters",

  // Enable/disable DevTools based on environment (optional)
  // You could use process.env.NODE_ENV !== 'production' here
  enabled: true,

  // Custom anonymous action type (optional)
  anonymousActionType: "UNKNOWN_ACTION",

  // Serialize options (optional - used for handling complex objects)
  serialize: {
    // Options for handling circular references
    options: true,
  },
};

// State persists across React Router navigations (module singleton) and
// resets naturally on page refresh — no localStorage needed.
export const useAdminProductsFiltersStore = create<AdminProductsFiltersState>()(
  // DevTools should be the outermost middleware
  devtools(
    (set) => ({
      // Initial state
      filters: initialFilters,

      // Actions
      setFilters: (newFilters) =>
        // The third parameter to set allows us to name the action for DevTools
        set(
          (state) => ({
            filters: { ...state.filters, ...newFilters },
          }),
          false,
          { type: "admin-products-filters/setFilters", payload: newFilters }
        ),

      clearFilters: () =>
        set(
          { filters: initialFilters },
          false,
          "admin-products-filters/clearFilters"
        ),

      setGridView: (gridView) =>
        set(
          (state) => ({
            filters: { ...state.filters, gridView },
          }),
          false,
          { type: "admin-products-filters/setGridView", payload: gridView }
        ),

      setSearchTerm: (searchAdminProductsTerm) =>
        set(
          (state) => ({
            filters: { ...state.filters, searchAdminProductsTerm },
          }),
          false,
          {
            type: "admin-products-filters/setSearchTerm",
            payload: searchAdminProductsTerm,
          }
        ),

      setCategoryFilters: (selectedCategoriesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedCategoriesFilter },
          }),
          false,
          {
            type: "admin-products-filters/setCategoryFilters",
            payload: selectedCategoriesFilter,
          }
        ),

      setMakeFilters: (selectedMakesFilter) =>
        set(
          (state) => ({
            filters: { ...state.filters, selectedMakesFilter },
          }),
          false,
          {
            type: "admin-products-filters/setMakeFilters",
            payload: selectedMakesFilter,
          }
        ),
    }),
    // Pass DevTools options as the second parameter to devtools
    devToolsOptions
  )
);
